// @flow
import { stringify } from 'query-string';
import * as Endpoints from './endpoints';

export function addGrid(clientId: Guid, name: string, sprocId: Guid) {
    return Endpoints.post(`grid`, { clientId, name, sprocId });
}

export function authenticateUser(): Promise<User> {
    return Endpoints.get('login');
}

export function deleteDashboard(dashboardId: Guid, clientId: Guid) {
    return Endpoints.del(`dashboard/${dashboardId}`, { clientId: clientId });
}

export function deleteFilter(filterId: Guid, clientId: Guid) {
    return Endpoints.del(`filter/${filterId}`, { clientId });
}

export function deleteFilterSession(sessionUid: Guid, clientId: Guid) {
    return Endpoints.del(`/masterFilter/session/${sessionUid}`, { clientId });
}

export function deleteGrid(gridId: Guid, clientId: Guid) {
    return Endpoints.del(`grid/${gridId}`, { clientId: clientId });
}

export function deleteReport(reportId: Guid, clientId: Guid) {
    return Endpoints.del(`report/${reportId}`, { clientId: clientId });
}

export function getAlerts(): Promise<Array<AlertDTO>> {
    return Endpoints.get('alert');
}

export function getAlert(alertId: number): Promise<AlertDTO> {
    return Endpoints.get(`alert/${alertId}`);
}

export function getConnections(clientId: Guid): Promise<Array<Connection>> {
    return Endpoints.get(`connections`, { clientId: clientId });
}

export function getDashboards(clientId: Guid): Promise<Dashboard[]> {
    return Endpoints.get('dashboard', { clientId: clientId });
}

export function getDeliveryMethods(): Promise<Array<DeliveryMethod>> {
    return Endpoints.getNotification(`DeliveryMethods`);
}

export function getGridData(
    gridId: Guid,
    clientId: Guid,
    filterId: ?Guid,
    showFilter: ?boolean
): Promise<{ data: any[], columnDefinitions: any[] }> {
    return Endpoints.get(`grid/${gridId}/data`, { filterId: filterId, clientId, showFilter });
}

export function getGridFilter(
    filterId: Guid,
    clientId: Guid
): Promise<{
    filterName: string,
    filterDefinition: string,
    data: any[],
    columnDefinitions: ColDef[],
    [string]: { text: string, value: string },
}> {
    return Endpoints.get(`grid/filter/${filterId}`, { clientId });
}

export function getGrids(clientId: Guid): Promise<any[]> {
    return Endpoints.get('grid', { clientId: clientId });
}

export function getMasterFilterSelectionList(clientId: Guid, type: string): Promise<any[]> {
    return Endpoints.get(`masterFilter/type/${type}/list`, { clientId });
}

export function getMasterFilterSessionDates(clientId: Guid, sessionUid: Guid): Promise<any[]> {
    return Endpoints.get(`masterFilter/session/${sessionUid}/date`, { clientId });
}

export function getMasterFilterSessionValues(clientId: Guid, sessionUid: Guid): Promise<any[]> {
    return Endpoints.get(`masterFilter/session/${sessionUid}/list`, { clientId });
}

export function getMasterFilterSession(clientId: Guid): Promise<any[]> {
    return Endpoints.get('masterFilter/session', { clientId: clientId });
}

export function getMasterFilterType(clientId: Guid): Promise<any[]> {
    return Endpoints.get('masterFilter/type', { clientId: clientId });
}

export function getNotificationUsers(): Promise<Array<User>> {
    return Endpoints.getNotification(`user/info`);
}

export function getReportData(reportId: Guid, clientId: Guid, filterId: ?Guid): Promise<any[][]> {
    return Endpoints.get(`report/${reportId}/data`, { filterId: filterId, clientId });
}

export function getReportFilter(
    filterId: Guid,
    clientId: Guid
): Promise<{
    filterName: string,
    filterDefinition: string,
    data: any[],
    columnDefinitions: ColDef[],
    [string]: { text: string, value: string },
}> {
    return Endpoints.get(`report/filter/${filterId}`, { clientId }); //TODO: implement api
}

export function getReports(clientId: Guid): Promise<Report[]> {
    return Endpoints.get('report', { clientId: clientId });
}

export function getStoredProcedures(
    clientId: Guid,
    connectionId: Guid
): Promise<Array<StoredProcedure>> {
    return Endpoints.get(`storedProcedures`, { connectionStringId: connectionId, clientId });
}

export function getTopics(): Promise<{ [string]: { id: number, name: string, filter: string }[] }> {
    throw new Error('Not implemented'); //TODO: implement
}

export function getUsers(clientId: Guid): Promise<UserPermissions[]> {
    return Endpoints.get('user', { clientId });
}

export function getVersionFlags(clientId: Guid): Promise<any> {
    return Endpoints.get('version-flags', { clientId });
}

export function patchDashboard(
    dashboardId: Guid,
    clientId: Guid,
    options: { description: string, statusId: number }
) {
    return Endpoints.patch(`dashboard/${dashboardId}`, options, { clientId });
}

export function patchFilter(
    filterId: Guid,
    filters: { [string]: string[] },
    name: string,
    clientId: Guid
): Promise<void> {
    return Promise.resolve()
        .then(() => {
            if (!filterId) {
                throw new Error('FilterId must be defined.');
            }
            return;
        })
        .then(() =>
            Endpoints.patch(`filter/${filterId}`, {
                filterId: filterId,
                name: name,
                definition: filters,
                clientId: clientId,
            })
        );
}

export function patchMasterFilterSession(
    clientId: Guid,
    isTemplate: boolean,
    templateName: string,
    sessionUid: Guid
) {
    return Endpoints.patch(
        `/masterFilter/session/${sessionUid}`,
        { isTemplate, templateName },
        { clientId }
    );
}

export function postMasterFilterSession(
    clientId: Guid,
    isTemplate: boolean,
    templateName: string
): Promise<any[]> {
    return Endpoints.post('masterFilter/session', { isTemplate, templateName }, { clientId });
}

export function postMasterFilterSelections(
    clientId: Guid,
    sessionUid: Guid,
    selectedFilters: any[],
    fromDate: Date,
    toDate: Date
): Promise<void> {
    return Endpoints.post(
        `masterFilter/session/${sessionUid}/list`,
        { selectedFilters, fromDate, toDate },
        { clientId }
    );
}

export function postNewFilter(
    gridId: Guid,
    filters: { [string]: string[] },
    name: string,
    clientId: Guid
): Promise<{ filters: { uID: Guid }[] }> {
    return Promise.resolve()
        .then(() => {
            if (!gridId) {
                throw new Error('GridId must be defined.');
            }
            return;
        })
        .then(() =>
            Endpoints.post(`filter`, {
                gridId: gridId,
                name: name,
                definition: filters,
                clientId: clientId,
            })
        );
}

export function previewFilter(filterId: Guid, clientId: Guid, filterDefinition: string) {
    return Endpoints.post(`/grid/filter/${filterId}/preview`, {
        filterId,
        clientId,
        filterDefinition,
    });
}

export function saveAlert(data: Object): Promise<{ rs: string }> {
    return Endpoints.post(`alert`, data);
}

export function sendAdHoc(data: Object): Promise<{ rs: string }> {
    return Endpoints.postNotification(`adHoc`, data);
}

export function shareAttachment(resource: string, data: Object): Promise<{ rs: string }> {
    return Endpoints.post(`messages/${resource}`, data);
}

export function updateDashboardUsers(
    clientId: Guid,
    dashboardId: Guid,
    permissions: Array<{ id: Guid, selected: boolean }>
) {
    return Endpoints.post(`user/dashboard/${dashboardId}`, permissions, { clientId });
}



// WEBPACK FOOTER //
// ./modules/client.js